import { useEffect, useState } from 'react';
import api from '../api/client.js';

/**
 * Hook compartido para administrar la edición en línea de partidos del fixture
 * (usado por FixtureEtapaDefinicion y FixtureEtapaGrupos).
 *
 * Maneja: partidos en edición, partido que se está grabando, errores por fila,
 * y la eliminación de un partido.
 *
 * @param {Object} params - { erroresPost, erroresPatch } de los hooks de red en uso
 */
const useEdicionFilaPartido = ({ erroresPost, erroresPatch } = {}) => {
  const [partidosEnEdicion, setPartidosEnEdicion] = useState({});
  const [partidoGrabandose, setPartidoGrabandose] = useState(null);
  const [erroresFilaPartido, setErroresFilaPartido] = useState({});

  useEffect(() => {
    if (erroresPost?.length > 0 || erroresPatch?.length > 0) {
      setPartidoGrabandose(null);
      if (partidoGrabandose != null) {
        setErroresFilaPartido((prev) => ({
          ...prev,
          [partidoGrabandose]: erroresPost?.length > 0 ? erroresPost : erroresPatch || [],
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erroresPost, erroresPatch, partidoGrabandose]);

  const armarPartidoEnEdicion = (partido, extra = {}) => {
    const golesLocalOriginal = partido.golesLocal ?? partido.resultadoLocal ?? null;
    const golesVisitanteOriginal = partido.golesVisitante ?? partido.resultadoVisitante ?? null;
    return {
      ...partido,
      golesLocalOriginal,
      golesVisitanteOriginal,
      localEditado: false,
      visitanteEditado: false,
      ...extra,
    };
  };

  const iniciarEdicion = (partido, extra = {}) => {
    setPartidosEnEdicion((prev) => ({
      ...prev,
      [partido.id]: armarPartidoEnEdicion(partido, extra),
    }));
  };

  const iniciarEdicionMultiple = (partidos, extraFn) => {
    setPartidosEnEdicion((prev) => {
      const nuevos = { ...prev };
      (partidos || []).forEach((partido) => {
        if (!nuevos[partido.id]) {
          nuevos[partido.id] = armarPartidoEnEdicion(partido, extraFn ? extraFn(partido) : {});
        }
      });
      return nuevos;
    });
  };

  const cancelarEdicion = (idPartido) => {
    setPartidosEnEdicion((prev) => {
      const nuevos = { ...prev };
      delete nuevos[idPartido];
      return nuevos;
    });
  };

  const limpiarEdicionMultiple = (ids) => {
    setPartidosEnEdicion((prev) => {
      const nuevos = { ...prev };
      (ids || []).forEach((id) => delete nuevos[id]);
      return nuevos;
    });
  };

  const actualizarEdicion = (idPartido, patch) => {
    setPartidosEnEdicion((prev) => ({
      ...prev,
      [idPartido]: { ...(prev[idPartido] || {}), ...patch },
    }));
  };

  const limpiarErrorFila = (id) => {
    setErroresFilaPartido((prev) => {
      const nuevos = { ...prev };
      delete nuevos[id];
      return nuevos;
    });
  };

  const eliminarPartido = async (idPartido, onEliminado) => {
    const confirmar = window.confirm('¿Está seguro que desea eliminar este partido?');
    if (!confirmar) {
      return;
    }

    try {
      const response = await api.delete(`partidos/${idPartido}`);
      if (response.ok) {
        onEliminado && onEliminado();
      } else {
        alert(`Error al eliminar partido: ${response.errors?.join(', ') || 'Error desconocido'}`);
      }
    } catch (error) {
      alert(`Error al eliminar partido: ${error.message}`);
    }
  };

  return {
    partidosEnEdicion,
    partidoGrabandose,
    erroresFilaPartido,
    setPartidoGrabandose,
    setErroresFilaPartido,
    iniciarEdicion,
    iniciarEdicionMultiple,
    cancelarEdicion,
    limpiarEdicionMultiple,
    actualizarEdicion,
    limpiarErrorFila,
    eliminarPartido,
  };
};

export default useEdicionFilaPartido;
